// EmailNotificationDetails Component - Shows email notification history for application
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Collapse,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import DoApiService from '../../../services/DoApiService';

const EmailNotificationDetails = ({ appId, isCab = false }) => {
    // State management
    const [emailNotifications, setEmailNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [viewBodyDialog, setViewBodyDialog] = useState(false);
    const [selectedEmailBody, setSelectedEmailBody] = useState(null);

    // Load email notifications when appId changes
    useEffect(() => {
        if (appId) {
            loadEmailNotifications();
        }
    }, [appId, isCab]);

    // Load email notifications
    const loadEmailNotifications = async () => {
        if (!appId) return;

        try {
            setLoading(true);
            setError(null);
            console.log('Loading email notifications for appId:', appId, 'isCab:', isCab);

            const response = await DoApiService.getEmailNotifications({
                appId: parseFloat(appId),
                isCab: isCab
            });
            
            if (response.success) {
                setEmailNotifications(response.data || []);
                console.log('Email notifications loaded:', response.data?.length || 0);
            } else {
                console.error('Failed to load email notifications:', response.message);
                setError('Failed to load email notifications');
            }
        } catch (error) {
            console.error('Error loading email notifications:', error);
            setError('Error loading email notifications: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
     if (!dateString) return 'N/A';
     try {
        const localDateString = dateString.includes('Z') ? dateString.replace('Z', '') : dateString;
        const date = new Date(localDateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
     } catch {
        return dateString;
     }
  };

    // Get status chip
    const getStatusChip = (status) => {
        if (status?.toLowerCase() === 'sent' || status?.toLowerCase() === 'success') {
            return (
                <Chip
                    icon={<CheckCircleIcon />}
                    label="Sent"
                    color="success"
                    size="small"
                    variant="outlined"
                />
            );
        } else if (status?.toLowerCase() === 'failed' || status?.toLowerCase() === 'error') {
            return (
                <Chip
                    icon={<ErrorIcon />}
                    label="Failed"
                    color="error"
                    size="small"
                    variant="outlined"
                />
            );
        } else if (status?.toLowerCase() === 'pending') {
            return (
                <Chip
                    icon={<ScheduleIcon />}
                    label="Pending"
                    color="warning"
                    size="small"
                    variant="outlined"
                />
            );
        } else {
            return (
                <Chip
                    label={status || 'Unknown'}
                    color="default"
                    size="small"
                    variant="outlined"
                />
            );
        }
    };

    // Handle row expand/collapse
    const handleRowExpand = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    // Handle view body dialog
    const handleViewBody = (emailBody) => {
        setSelectedEmailBody(emailBody);
        setViewBodyDialog(true);
    };

    const handleCloseBodyDialog = () => {
        setViewBodyDialog(false);
        setSelectedEmailBody(null);
    };

    // Strip HTML tags for preview
    const getEmailPreview = (htmlBody) => {
        if (!htmlBody) return 'No content';
        const div = document.createElement('div');
        div.innerHTML = htmlBody;
        const text = div.textContent || div.innerText || '';
        return text.length > 100 ? text.substring(0, 100) + '...' : text;
    };

    if (!appId) {
        return (
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" color="textSecondary" sx={{ textAlign: 'center' }}>
                        No application selected for email notification details
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ 
            borderRadius: 3,
            mt: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0'
        }}>
            <CardContent sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Box sx={{ 
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        borderRadius: 2,
                        p: 1.5,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <EmailIcon sx={{ fontSize: 24, color: 'white' }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                            Email Notification History
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Track all email notifications sent for this application
                        </Typography>
                    </Box>
                </Box>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Loading email notifications...
                        </Typography>
                    </Box>
                )}

                {/* Email Notifications Table */}
                {!loading && (
                    <>
                        {emailNotifications.length > 0 ? (
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    borderRadius: 2,
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    maxHeight: 500
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ 
                                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                                        }}>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>S.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>From</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>To (Recipient)</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Subject</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Sent Date</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Attachment</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {emailNotifications.map((notification, index) => (
                                            <React.Fragment key={index}>
                                                <TableRow 
                                                    hover
                                                    sx={{ 
                                                        '&:hover': { backgroundColor: '#f8fafc' },
                                                        '&:nth-of-type(odd)': { backgroundColor: '#fafbfc' }
                                                    }}
                                                >
                                                  
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {notification.emailFrom || 'noreply@nabl.qcin.org'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'action.active' }} />
                                                            <Typography variant="body2">
                                                                {notification.emailTo || notification.recipient || notification.toEmail || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                                            {notification.subject || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {getStatusChip(notification.status || notification.emailStatus)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formatDate(notification.sentDate || notification.insertDate || notification.createdDate)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {notification.attachment ? 
                                                                <Chip label="Yes" color="primary" size="small" variant="outlined" /> : 
                                                                <Chip label="No" color="default" size="small" variant="outlined" />
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleViewBody(notification.body)}
                                                            title="View Email Body"
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Paper sx={{ 
                                textAlign: 'center', 
                                py: 6, 
                                px: 4,
                                bgcolor: '#fafbfc',
                                border: '2px dashed #e2e8f0',
                                borderRadius: 2
                            }}>
                                <Box sx={{ 
                                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                    borderRadius: '50%',
                                    width: 80,
                                    height: 80,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 3
                                }}>
                                    <EmailIcon sx={{ fontSize: 40, color: 'white' }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                                    No Email Notifications Found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                                    No email notifications have been sent for this application yet.
                                </Typography>
                            </Paper>
                        )}
                    </>
                )}

                {/* Summary */}
                {!loading && emailNotifications.length > 0 && (
                    <Paper sx={{ 
                        mt: 3, 
                        p: 3, 
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        borderRadius: 2 
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmailIcon sx={{ mr: 2, fontSize: 24 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Total Email Notifications: {emailNotifications.length}
                            </Typography>
                        </Box>
                    </Paper>
                )}

                {/* Email Body View Dialog */}
                <Dialog
                    open={viewBodyDialog}
                    onClose={handleCloseBodyDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6">Email Content</Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedEmailBody ? (
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: '#fafbfc',
                                    borderRadius: 1,
                                    border: '1px solid #e2e8f0',
                                    minHeight: 200,
                                    maxHeight: 500,
                                    overflow: 'auto'
                                }}
                                dangerouslySetInnerHTML={{ __html: selectedEmailBody }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No email content available
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseBodyDialog} variant="outlined">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default EmailNotificationDetails;
